import { ReactComponent as LogoIcon } from '@app/assets/icons/logo.svg';
import { NavBar, SiteSearch, JoinButton } from '@app/components';
import { ReactComponent as SearchIcon } from '@app/assets/icons/search.svg';
import { LocalStorageItem, SiteMap } from '@app/constants';
import { NAVBAR_DATA } from '@app/data/navbar.data';
import { ScrollDirection, useEnhancedTranslation, useMedia, useScrollDirection } from '@app/hooks';
import cx from 'classnames';
import { useMemo, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { HeaderContext, HeaderContextProvider } from './header.context';
import { setBadgeFromStorage } from './header.utils';
import { MobileHeader } from './components/MobileHeader';
import { DesktopHeader } from './components/DesktopHeader';

import './header.scss';
import '@app/index.scss';

const HeaderContent = (): JSX.Element => {
  const { t } = useEnhancedTranslation({ keyPrefix: 'header' });
  const { isDesktop, isPortable } = useMedia();
  const { isBadgeVisible, setIsBadgeVisible } = useContext(HeaderContext);
  const { scrollDirection, scrollYOffset } = useScrollDirection(15);

  useEffect(() => {
    setIsBadgeVisible && setBadgeFromStorage(setIsBadgeVisible);
  }, [setIsBadgeVisible]);

  useEffect(() => {
    localStorage.setItem(LocalStorageItem.BADGE_VISIBLE, isBadgeVisible ? 'true' : 'false');
  }, [isBadgeVisible]);

  const ResponsibleNavbar = useMemo(
    () => (
      <NavBar
        data={isPortable ? NAVBAR_DATA : NAVBAR_DATA.filter((item) => item.link !== SiteMap.CERTIFICATES)}
        className="header__nav"
        listClassName="header__links"
        itemClassName="header__link"
      />
    ),
    [isPortable],
  );

  return (
    <header
      className={cx('header', {
        'header--hidden': scrollDirection === ScrollDirection.DOWN,
        'header--filled': scrollYOffset > window.innerHeight / 8,
      })}
      data-testid="header"
    >
      <Link to={SiteMap.HOME}>
        <LogoIcon className="header__logo" data-testid="logo" />
      </Link>
      <div style={{ display: isDesktop ? undefined : 'none' }} className="header__nav-n-search">
        {ResponsibleNavbar}
        <SiteSearch />
      </div>
      <div className="header__right">
        {isDesktop && <DesktopHeader />}
        {!isDesktop && (
          <Link to={SiteMap.SEARCH} className="header__control header__search-link">
            <SearchIcon title={t('search') || undefined} />
          </Link>
        )}
        {isDesktop && <JoinButton />}
        {isPortable && <MobileHeader navbar={ResponsibleNavbar} />}
      </div>
    </header>
  );
};

export const Header = (): JSX.Element => {
  return (
    <HeaderContextProvider>
      <HeaderContent />
    </HeaderContextProvider>
  );
};
